import {
  Risk_Assessment_Result_Control_Type_Enum,
  Risk_Scoring_Model_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { useAggregation } from '@/hooks/useAggregation';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { useRiskScore } from '@/hooks/useRiskScore';
import { toLocalDate } from '@/utils/dateUtils';

import { RiskScoreBreakdownPopover } from '../../risk-score-breakdown/RiskScoreBreakdownPopover';
import type { RatingSectionData, RiskOverviewPanelViewState } from '../types';

export const useRiskOverviewPanelSections = (
  riskId: string
): RiskOverviewPanelViewState => {
  const { t } = useTranslation('common', { keyPrefix: 'riskOverviewPanel' });
  const score = useRiskScore(riskId);
  const { riskModel } = useAggregation();
  const { resolveRiskRating, resolveLikelihood, resolveImpact } =
    useRiskRatingResolver('standard', { configType: 'risk_assessment' });

  const {
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    breakdownData,
  } = score;

  const isTypedModel =
    riskModel === Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages;
  const isBreakdownModuleEnabled = useIsModuleEnabled(
    'risk.subModules.risk_score_breakdown'
  );
  const showBreakdownPopover =
    isBreakdownModuleEnabled && isTypedModel && breakdownData !== null;

  const sections = useMemo((): RatingSectionData[] => {
    const formatDate = (date: null | string | undefined) =>
      date ? toLocalDate(date) : undefined;

    return [
      {
        id: 'inherent',
        title: t('inherentTitle'),
        overallRating: resolveRiskRating({
          likelihood: inherentLikelihood ?? null,
          impact: inherentImpact ?? null,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
          rating: inherentRating,
        }),
        formattedDate: formatDate(inherentCompletionDate),
        rows: [
          {
            label: t('likelihoodLabel'),
            rating: resolveLikelihood(inherentLikelihood),
            formattedDate: formatDate(inherentCompletionDate),
          },
          {
            label: t('impactLabel'),
            rating: resolveImpact(inherentImpact),
            formattedDate: formatDate(inherentCompletionDate),
          },
        ],
      },
      {
        id: 'residual',
        title: t('residualTitle'),
        overallRating: resolveRiskRating({
          likelihood: residualLikelihood ?? null,
          impact: residualImpact ?? null,
          controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
          rating: residualRating,
        }),
        formattedDate: formatDate(residualCompletionDate),
        rows: [
          {
            label: t('likelihoodLabel'),
            rating: resolveLikelihood(residualLikelihood),
            formattedDate: formatDate(residualCompletionDate),
          },
          {
            label: t('impactLabel'),
            rating: resolveImpact(residualImpact),
            formattedDate: formatDate(residualCompletionDate),
          },
        ],
        adornment:
          showBreakdownPopover && breakdownData ? (
            <RiskScoreBreakdownPopover
              breakdown={breakdownData}
              triggerLabel={t('breakdownPopoverTriggerLabel', {
                defaultValue: 'Why this score?',
              })}
            />
          ) : undefined,
      },
    ];
  }, [
    t,
    inherentRating,
    inherentLikelihood,
    inherentImpact,
    inherentCompletionDate,
    residualRating,
    residualLikelihood,
    residualImpact,
    residualCompletionDate,
    resolveRiskRating,
    resolveLikelihood,
    resolveImpact,
    showBreakdownPopover,
    breakdownData,
  ]);

  return {
    loading: score.loading,
    sections,
  };
};
